import React from 'react'
import {
 User, Phone, MessageSquare, Eye, Mail, Calendar,
 Edit, Trash2, Car, UserCircle, Users, Plus
} from 'lucide-react'
import { Lead } from '../../types'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, StatusBadge } from '../ui'

interface LeadsListViewProps {
 leads: Lead[]
 isLoading?: boolean
 onViewLead?: (lead: Lead) => void
 onEditLead: (lead: Lead) => void
 onDeleteLead: (leadId: string) => void
 onAddLead: () => void
 searchTerm?: string
 statusFilter?: string
}

const sourceIcon = (source: Lead['source']) => {
 switch (source) {
 case 'whatsapp': return <MessageSquare className="h-3.5 w-3.5 text-emerald-500" />
 case 'phone': return <Phone className="h-3.5 w-3.5 text-accent-500" />
 case 'website': return <Eye className="h-3.5 w-3.5 text-purple-500" />
 default: return <User className="h-3.5 w-3.5 text-muted-foreground" />
 }
}

const sourceLabel: Record<string, string> = {
 website: 'Site', whatsapp: 'WhatsApp', phone: 'Telefone',
 'walk-in': 'Presencial', email: 'Email', referral: 'Indicação',
 'social-media': 'Rede Social', other: 'Outro'
}

/* ────────────────────────────────────────────────────────────── */
/* Skeleton loader */
/* ────────────────────────────────────────────────────────────── */
const SkeletonCard = () => (
 <div className="bg-card border border-border rounded-lg p-4 space-y-3 animate-pulse">
 <div className="flex items-center gap-3">
 <div className="w-9 h-9 rounded-full bg-muted flex-shrink-0" />
 <div className="flex-1 space-y-1.5">
 <div className="h-3.5 bg-muted rounded w-2/5" />
 <div className="h-3 bg-muted rounded w-1/3" />
 </div>
 <div className="h-5 w-16 bg-muted rounded-full" />
 </div>
 <div className="h-3 bg-muted rounded w-3/5" />
 <div className="h-3 bg-muted rounded w-2/5" />
 </div>
)

/* ────────────────────────────────────────────────────────────── */
/* Mobile card */
/* ────────────────────────────────────────────────────────────── */
const LeadCard: React.FC<{
 lead: Lead
 onView?: (l: Lead) => void
 onEdit: (l: Lead) => void
 onDelete: (id: string) => void
}> = ({ lead, onView, onEdit, onDelete }) => {
 const primaryPhone = lead.phones?.[0] ?? null
 const primaryEmail = lead.emails?.[0] ?? null
 const firstVehicle = lead.vehicleInterest?.[0] ?? null

 return (
 <div
 className="bg-card border border-border rounded-lg p-4 space-y-3 cursor-pointer hover:border-accent-200 dark:hover:border-accent-800 hover:shadow-precision-md transition-all duration-150 active:scale-[0.99]"
 onClick={() => onView?.(lead)}
 >
 {/* Row 1 — name + status */}
 <div className="flex items-start justify-between gap-2">
 <div className="flex items-center gap-2.5 min-w-0">
 <div className="w-9 h-9 rounded-full bg-accent/10 dark:bg-primary-800 flex items-center justify-center flex-shrink-0">
 <User className="h-4 w-4 text-accent dark:text-accent" />
 </div>
 <div className="min-w-0">
 <p className="text-sm font-semibold text-foreground truncate">{lead.name}</p>
 <div className="flex items-center gap-1 mt-0.5">
 {sourceIcon(lead.source)}
 <span className="text-xs text-muted-foreground">
 {sourceLabel[lead.source] || lead.source}
 </span>
 </div>
 </div>
 </div>
 <StatusBadge status={lead.status} />
 </div>

 {/* Row 2 — contact */}
 <div className="space-y-1">
 {primaryPhone && (
 <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
 <Phone className="h-3 w-3 flex-shrink-0" />
 <span>{primaryPhone.phone}</span>
 {primaryPhone.isWhatsApp && (
 <MessageSquare className="h-3 w-3 text-emerald-500 flex-shrink-0" />
 )}
 {(lead.phones?.length ?? 0) > 1 && (
 <span className="text-accent-600 font-medium">+{(lead.phones?.length ?? 0) - 1}</span>
 )}
 </div>
 )}
 {primaryEmail && (
 <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
 <Mail className="h-3 w-3 flex-shrink-0" />
 <span className="truncate">{primaryEmail.email}</span>
 </div>
 )}
 </div>

 {/* Row 3 — vehicle + seller + date + actions */}
 <div className="flex items-center justify-between gap-2 pt-1 border-t border-border">
 <div className="flex items-center gap-3 min-w-0 flex-1">
 {firstVehicle ? (
 <div className="flex items-center gap-1 text-xs text-muted-foreground min-w-0">
 <Car className="h-3 w-3 flex-shrink-0 text-accent-500" />
 <span className="truncate">{firstVehicle.vehicle.name}</span>
 {(lead.vehicleInterest?.length ?? 0) > 1 && (
 <span className="text-accent-600 font-medium flex-shrink-0">
 +{(lead.vehicleInterest?.length ?? 0) - 1}
 </span>
 )}
 </div>
 ) : (
 <span className="text-xs text-muted-foreground/60">Sem veículo</span>
 )}
 </div>

 <div className="flex items-center gap-1 flex-shrink-0">
 <span className="text-xs text-muted-foreground">
 {new Date(lead.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
 </span>
 <button
 type="button"
 onClick={(e) => { e.stopPropagation(); onEdit(lead) }}
 className="p-1.5 rounded-md text-muted-foreground hover:text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-colors duration-100"
 aria-label="Editar"
 >
 <Edit className="h-3.5 w-3.5" />
 </button>
 <button
 type="button"
 onClick={(e) => { e.stopPropagation(); onDelete(lead.id) }}
 className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-100"
 aria-label="Excluir"
 >
 <Trash2 className="h-3.5 w-3.5" />
 </button>
 </div>
 </div>
 </div>
 )
}

/* ────────────────────────────────────────────────────────────── */
/* Main component */
/* ────────────────────────────────────────────────────────────── */
export const LeadsListView: React.FC<LeadsListViewProps> = ({
 leads,
 isLoading,
 onViewLead,
 onEditLead,
 onDeleteLead,
 onAddLead,
 searchTerm,
 statusFilter,
}) => {
 /* Loading state */
 if (isLoading) {
 return (
 <>
 {/* Mobile skeletons */}
 <div className="md:hidden space-y-3">
 {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
 </div>
 {/* Desktop skeleton rows */}
 <div className="hidden md:block">
 <div className="space-y-2">
 {[...Array(5)].map((_, i) => (
 <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />
 ))}
 </div>
 </div>
 </>
 )
 }

 /* Empty state */
 if (leads.length === 0) {
 return (
 <div className="flex flex-col items-center justify-center py-16 text-center">
 <div className="w-14 h-14 rounded-2xl bg-accent/5 dark:bg-primary-800 flex items-center justify-center mb-4">
 <Users className="h-7 w-7 text-accent" />
 </div>
 <p className="text-sm font-medium text-foreground mb-1">
 {searchTerm || (statusFilter && statusFilter !== 'all')
 ? 'Nenhum lead encontrado'
 : 'Nenhum lead cadastrado ainda'}
 </p>
 <p className="text-xs text-muted-foreground mb-4">
 {searchTerm || (statusFilter && statusFilter !== 'all')
 ? 'Tente ajustar os filtros'
 : 'Comece adicionando o primeiro lead'}
 </p>
 {!searchTerm && (!statusFilter || statusFilter === 'all') && (
 <button
 type="button"
 onClick={onAddLead}
 className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-accent-600 text-white hover:bg-accent-700 transition-all duration-150"
 >
 <Plus className="h-4 w-4" />
 Adicionar Lead
 </button>
 )}
 </div>
 )
 }

 return (
 <>
 {/* ── Mobile: card list ── */}
 <div className="md:hidden space-y-3">
 {leads.map((lead) => (
 <LeadCard
 key={lead.id}
 lead={lead}
 onView={onViewLead}
 onEdit={onEditLead}
 onDelete={onDeleteLead}
 />
 ))}
 </div>

 {/* ── Desktop: table ── */}
 <div className="hidden md:block rounded-lg border border-border overflow-hidden">
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>Cliente</TableHead>
 <TableHead>Veículo de Interesse</TableHead>
 <TableHead>Contato</TableHead>
 <TableHead>Vendedor</TableHead>
 <TableHead>Origem</TableHead>
 <TableHead>Status</TableHead>
 <TableHead>Data</TableHead>
 <TableHead className="w-20">Ações</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {leads.map((lead) => (
 <TableRow
 key={lead.id}
 hover
 onClick={() => onViewLead?.(lead)}
 className="cursor-pointer"
 >
 {/* Cliente */}
 <TableCell>
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 rounded-full bg-accent/10 dark:bg-primary-800 flex items-center justify-center flex-shrink-0">
 <User className="h-4 w-4 text-accent dark:text-accent" />
 </div>
 <div>
 <p className="text-sm font-medium text-foreground">{lead.name}</p>
 {lead.notes && (
 <p className="text-xs text-muted-foreground truncate max-w-[160px]">
 {lead.notes.substring(0, 50)}{lead.notes.length > 50 ? '…' : ''}
 </p>
 )}
 </div>
 </div>
 </TableCell>

 {/* Veículo */}
 <TableCell>
 {lead.vehicleInterest && lead.vehicleInterest.length > 0 ? (
 <div className="flex items-center gap-1.5">
 <Car className="h-3.5 w-3.5 text-accent-500 flex-shrink-0" />
 <div className="flex flex-wrap gap-1">
 {lead.vehicleInterest.slice(0, 2).map((vi) => (
 <span
 key={vi.id}
 className="text-xs font-medium text-foreground bg-accent/5 dark:bg-primary-800 px-2 py-0.5 rounded"
 >
 {vi.vehicle.name}
 </span>
 ))}
 {lead.vehicleInterest.length > 2 && (
 <span className="text-xs text-accent-600 font-medium">
 +{lead.vehicleInterest.length - 2}
 </span>
 )}
 </div>
 </div>
 ) : (
 <span className="text-xs text-muted-foreground">—</span>
 )}
 </TableCell>

 {/* Contato */}
 <TableCell>
 <div className="space-y-1">
 {(lead.phones?.[0] ?? lead.phone) && (
 <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
 <Phone className="h-3 w-3 flex-shrink-0" />
 <span>{lead.phones?.[0]?.phone ?? lead.phone}</span>
 {lead.phones?.[0]?.isWhatsApp && (
 <MessageSquare className="h-3 w-3 text-emerald-500" />
 )}
 {(lead.phones?.length ?? 0) > 1 && (
 <span className="text-accent-600">+{(lead.phones?.length ?? 0) - 1}</span>
 )}
 </div>
 )}
 {(lead.emails?.[0] ?? lead.email) && (
 <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
 <Mail className="h-3 w-3 flex-shrink-0" />
 <span className="truncate max-w-[140px]">
 {lead.emails?.[0]?.email ?? lead.email}
 </span>
 </div>
 )}
 </div>
 </TableCell>

 {/* Vendedor */}
 <TableCell>
 {(lead as any).sellerName ? (
 <div className="flex items-center gap-1.5 text-xs">
 <UserCircle className="h-3.5 w-3.5 text-muted-foreground" />
 <span className="text-foreground">{(lead as any).sellerName}</span>
 </div>
 ) : (
 <span className="text-xs text-muted-foreground">—</span>
 )}
 </TableCell>

 {/* Origem */}
 <TableCell>
 <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
 {sourceIcon(lead.source)}
 <span className="capitalize">{sourceLabel[lead.source] || lead.source}</span>
 </div>
 </TableCell>

 {/* Status */}
 <TableCell>
 <StatusBadge status={lead.status} />
 </TableCell>

 {/* Data */}
 <TableCell>
 <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
 <Calendar className="h-3 w-3" />
 {new Date(lead.createdAt).toLocaleDateString('pt-BR')}
 </div>
 </TableCell>

 {/* Ações */}
 <TableCell>
 <div className="flex gap-1">
 <button
 type="button"
 onClick={(e) => { e.stopPropagation(); onEditLead(lead) }}
 className="p-1.5 rounded-md text-muted-foreground hover:text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-colors duration-100"
 title="Editar"
 >
 <Edit className="h-3.5 w-3.5" />
 </button>
 <button
 type="button"
 onClick={(e) => { e.stopPropagation(); onDeleteLead(lead.id) }}
 className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-100"
 title="Excluir"
 >
 <Trash2 className="h-3.5 w-3.5" />
 </button>
 </div>
 </TableCell>
 </TableRow>
 ))}
 </TableBody>
 </Table>
 </div>
 </>
 )
}

